/**
 * useAdminApi50.ts — Admin API hooks and types for AT-50.02
 *
 * Purpose:
 *   Provides TypeScript types for billing plans and plan tiers, plus
 *   React Query hooks for plan CRUD operations and customer overview
 *   data used by the Plan & Customer Management screens.
 *
 * Public API:
 *   - Plan               Billing plan shape returned by /admin/plans
 *   - PlanTier           Tier pricing row within a tiered plan
 *   - CustomerOverview   Composite overview returned by /admin/customers/:id/overview
 *   - useAdminPlansAdmin Plan list + mutations
 *   - useCustomerOverview Customer composite view
 *
 * Architecture Context:
 *   Extends useAdminApi.ts (AT-50.01) with plan write operations and the
 *   customer overview endpoint added by ATP 20.06. Direct fetch is used
 *   because BillingClient does not yet expose these admin-only routes.
 *
 * @module packages/billing-admin/src/hooks/useAdminApi50
 * @since 2026-06-01 (AT-50.02 — Plan & Customer Management Screens)
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import type { AuditLogPage, AuditLogQuery } from '@rule42/billing-types';

// ---------------------------------------------------------------------------
// Plan types
// ---------------------------------------------------------------------------

/**
 * A single pricing tier for plans with pricing_model='tiered'.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export interface PlanTier {
  /** First unit quantity in the tier (inclusive). */
  min_qty: number;
  /** Last unit quantity in the tier (inclusive); null = unlimited. */
  max_qty: number | null;
  /** Per-unit price within the tier. */
  unit_price: number;
}

/**
 * Billing plan as returned by GET /admin/plans and GET /admin/plans/:id.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export interface Plan {
  /** Unique plan identifier. */
  id: string;
  /** Owning tenant identifier (null for global master plans). */
  tenant_id: string | null;
  /** Human-readable plan name. */
  name: string;
  /** Pricing model variant. */
  pricing_model: 'flat' | 'per_seat' | 'tiered' | 'usage';
  /** Base price for flat / per-seat models. */
  price: number;
  /** ISO 4217 currency code. */
  currency: string;
  /** Billing interval. */
  interval: 'monthly' | 'annual';
  /** Canonical catalog seed key when the row came from seeded materialization. */
  seed_key?: string;
  /** Per-seat price when pricing_model='per_seat'. */
  seat_price?: number;
  /** Tiers when pricing_model='tiered'. */
  tiers?: PlanTier[];
  /** Optional feature list. */
  features?: string[];
  /** Plan lifecycle status. */
  status: 'active' | 'archived';
  /** For usage-model plans: ID of the assigned pricing catalog. */
  pricing_catalog_id?: string | null;
  /** Multi-model pricing configurations (from plan_pricing_models table). */
  pricing_models?: PlanPricingModel[];
}

/**
 * A single pricing model attached to a plan. Plans can stack multiple models
 * (e.g. flat base fee + usage metering + per-seat add-on).
 */
export interface PlanPricingModel {
  id: string;
  plan_id: string;
  model_type: 'flat' | 'per_seat' | 'usage' | 'tiered';
  /**
   * Model-specific configuration:
   * - flat: { amount_cents, currency }
   * - per_seat: { per_seat_cents, currency }
   * - usage: { pricing_catalog_id, billing_mode: 'credits'|'arrears' }
   * - tiered: { tiers, currency }
   */
  config: Record<string, unknown>;
  display_order: number;
  is_active: boolean;
}

export interface CatalogPlanPrice {
  id: string;
  interval: 'monthly' | 'annual';
  amount: number;
  currency: string;
  per_unit_rate?: number;
  tiers?: Array<{ up_to: number | null; unit_amount: number }>;
}

export interface CatalogPlan {
  id: string;
  name: string;
  pricing_model: 'flat' | 'per_seat' | 'tiered' | 'usage_based';
  trial_days?: number;
  is_default_trial_target?: boolean;
  prices: CatalogPlanPrice[];
}

export interface PlanCatalogOverview {
  configPath: string;
  catalog: CatalogPlan[];
}

/**
 * Active subscription summary within a customer overview.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export interface SubscriptionSummary {
  /** Subscription identifier. */
  id: string;
  /** Plan name. */
  plan_name: string;
  /** Subscription lifecycle status. */
  status: 'active' | 'trialing' | 'past_due' | 'cancelled';
}

/**
 * Credit balance entry within a customer overview.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export interface CreditEntry {
  /** Credit type identifier (e.g. 'promotional', 'refund'). */
  credit_type: string;
  /** Remaining balance for this credit type. */
  balance: number;
  /** Currency code. */
  currency: string;
}

/**
 * Invoice summary within a customer overview.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export interface InvoiceSummary {
  /** Invoice identifier. */
  id: string;
  /** Invoice total amount. */
  amount: number;
  /** Currency code. */
  currency: string;
  /** Invoice status. */
  status: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible';
  /** ISO date string. */
  created_at: string;
}

/**
 * Customer profile within the overview response.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export interface CustomerProfile {
  /** Customer identifier. */
  id: string;
  /** Customer display name. */
  name: string;
  /** Contact email address. */
  email: string;
  /** ISO date string when the customer was created. */
  created_at: string;
}

/**
 * Composite customer overview returned by GET /admin/customers/:id/overview.
 *
 * All four sections are delivered in a single API call per the directive.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export interface CustomerOverview {
  /** Customer profile information. */
  profile: CustomerProfile;
  /** Active subscription details; null if none. */
  subscription: SubscriptionSummary | null;
  /** Credit balance breakdown by type. */
  credits: CreditEntry[];
  /** Last 5 invoices. */
  invoices: InvoiceSummary[];
}

export interface EnterpriseContract {
  id: string;
  customer_id: string;
  customer_name: string;
  po_number: string;
  contract_value: number;
  billing_interval: 'monthly' | 'quarterly' | 'annual';
  start_date: string;
  end_date: string;
  status: string;
}

export interface UnmatchedTransaction {
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
  bank_reference: string;
}

export interface MrrReportPoint {
  month: string;
  mrr: number;
  newMrr: number;
  expansionMrr: number;
  contractionMrr: number;
}

export interface ArrReportSummary {
  arr: number;
  churnRate: number;
}

export interface CohortRetention {
  cohort: string;
  month0: number;
  month1: number;
  month2: number;
  month3: number;
  month4: number;
  month5: number;
  month6: number;
}

interface Envelope<T> {
  data: T;
}

interface CollectionEnvelope<T> {
  data: T[];
}

type EnterpriseContractPayload = {
  customerId: string;
  contractValue: number;
  billingInterval: 'monthly' | 'quarterly' | 'annual' | 'custom';
  startDate: string;
  endDate?: string;
  poNumber?: string;
  currency?: string;
};

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * useAdminToken — retrieves token and apiBaseUrl for raw fetch operations.
 *
 * @returns Token string and API base URL
 * @since 2026-06-01 (AT-50.02)
 */
function useAdminToken(): { token: string | null; apiBaseUrl: string } {
  const { token, apiBaseUrl } = useAdminAuth();
  return useMemo(() => ({ token, apiBaseUrl }), [token, apiBaseUrl]);
}

async function authenticatedFetch<T>(
  apiBaseUrl: string,
  token: string,
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  if (!res.ok) {
    throw new Error(`Request failed: ${res.status}`);
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json() as Promise<T>;
}

function monthLabel(date: Date): string {
  return date.toISOString().slice(0, 7);
}

function daysAgo(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Plan hooks
// ---------------------------------------------------------------------------

/**
 * useAdminPlansList — returns the full plan list from GET /admin/plans.
 *
 * @since 2026-06-01 (AT-50.02)
 */
export function useAdminPlansList() {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<Plan[], Error>({
    queryKey: ['admin', 'plans', 'list'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch plans: ${res.status}`);
      return res.json() as Promise<Plan[]>;
    },
    enabled: !!token,
  });
}

export function useAdminPlanCatalog() {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<PlanCatalogOverview, Error>({
    queryKey: ['admin', 'plans', 'catalog'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      return authenticatedFetch<PlanCatalogOverview>(apiBaseUrl, token, '/admin/plans/catalog');
    },
    enabled: !!token,
  });
}

/**
 * useArchivePlan — mutation that PATCH /admin/plans/:id with {status:'archived'}.
 *
 * @returns React Query mutation object
 * @since 2026-06-01 (AT-50.02)
 */
export function useArchivePlan() {
  const { token, apiBaseUrl } = useAdminToken();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (planId: string) => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/plans/${planId}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: 'archived' }),
      });
      if (!res.ok) throw new Error(`Failed to archive plan: ${res.status}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

/**
 * useDeletePlan — mutation that DELETE /admin/plans/:id (hard-delete).
 */
export function useDeletePlan() {
  const { token, apiBaseUrl } = useAdminToken();
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (planId: string) => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/plans/${planId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to delete plan: ${res.status}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'plans'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Plan pricing models hooks (multi-model stacking)
// ---------------------------------------------------------------------------

export function usePlanPricingModels(planId: string | null) {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<PlanPricingModel[], Error>({
    queryKey: ['admin', 'plans', planId, 'pricing-models'],
    enabled: !!token && !!planId,
    queryFn: async () => {
      const res = await fetch(`${apiBaseUrl}/admin/plans/${planId}/pricing-models`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to fetch pricing models: ${res.status}`);
      const json = await res.json();
      return json.data;
    },
  });
}

export function useAddPricingModel() {
  const { token, apiBaseUrl } = useAdminToken();
  const qc = useQueryClient();
  return useMutation<PlanPricingModel, Error, { planId: string; model_type: string; config: Record<string, unknown>; display_order?: number }>({
    mutationFn: async ({ planId, model_type, config, display_order }) => {
      const res = await fetch(`${apiBaseUrl}/admin/plans/${planId}/pricing-models`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ model_type, config, display_order }),
      });
      if (!res.ok) throw new Error(`Failed to add pricing model: ${res.status}`);
      const json = await res.json();
      return json.data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'plans', vars.planId, 'pricing-models'] });
    },
  });
}

export function useUpdatePricingModel() {
  const { token, apiBaseUrl } = useAdminToken();
  const qc = useQueryClient();
  return useMutation<PlanPricingModel, Error, { planId: string; modelId: string; updates: Record<string, unknown> }>({
    mutationFn: async ({ planId, modelId, updates }) => {
      const res = await fetch(`${apiBaseUrl}/admin/plans/${planId}/pricing-models/${modelId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error(`Failed to update pricing model: ${res.status}`);
      const json = await res.json();
      return json.data;
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'plans', vars.planId, 'pricing-models'] });
    },
  });
}

export function useDeletePricingModel() {
  const { token, apiBaseUrl } = useAdminToken();
  const qc = useQueryClient();
  return useMutation<void, Error, { planId: string; modelId: string }>({
    mutationFn: async ({ planId, modelId }) => {
      const res = await fetch(`${apiBaseUrl}/admin/plans/${planId}/pricing-models/${modelId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to delete pricing model: ${res.status}`);
    },
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['admin', 'plans', vars.planId, 'pricing-models'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Customer overview hook
// ---------------------------------------------------------------------------

/**
 * useCustomerOverview — fetches composite customer overview from
 * GET /admin/customers/:id/overview.
 *
 * Returns profile, active subscription, credit balance, and last 5 invoices
 * in a single API call per the AT-50.02 directive.
 *
 * @param customerId - Customer identifier to look up
 * @since 2026-06-01 (AT-50.02)
 */
export function useCustomerOverview(customerId: string) {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<CustomerOverview, Error>({
    queryKey: ['admin', 'customers', customerId, 'overview'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/customers/${customerId}/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok)
        throw new Error(`Failed to fetch customer overview: ${res.status}`);
      return res.json() as Promise<CustomerOverview>;
    },
    enabled: !!token && !!customerId,
  });
}

export function useEnterpriseContracts(customerId?: string) {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<EnterpriseContract[], Error>({
    queryKey: ['admin', 'enterprise-contracts', customerId ?? 'all'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const query = customerId ? `?customerId=${encodeURIComponent(customerId)}` : '';
      const response = await authenticatedFetch<CollectionEnvelope<EnterpriseContract>>(
        apiBaseUrl,
        token,
        `/admin/enterprise/contracts${query}`,
      );
      return response.data;
    },
    enabled: !!token,
  });
}

export async function createContract(
  payload: Omit<EnterpriseContract, 'id' | 'customer_name' | 'status'>,
  token: string,
): Promise<EnterpriseContract> {
  const apiBaseUrl = window.location.origin;
  const requestPayload: EnterpriseContractPayload = {
    customerId: payload.customer_id,
    contractValue: payload.contract_value,
    billingInterval: payload.billing_interval,
    startDate: payload.start_date,
    ...(payload.end_date ? { endDate: payload.end_date } : {}),
    ...(payload.po_number ? { poNumber: payload.po_number } : {}),
    currency: 'USD',
  };
  const response = await authenticatedFetch<Envelope<EnterpriseContract>>(
    apiBaseUrl,
    token,
    '/admin/enterprise/contracts',
    {
      method: 'POST',
      body: JSON.stringify(requestPayload),
    },
  );
  return response.data;
}

export async function updateContract(
  contractId: string,
  payload: Omit<EnterpriseContract, 'id' | 'customer_name' | 'status'>,
  token: string,
): Promise<EnterpriseContract> {
  const apiBaseUrl = window.location.origin;
  const requestPayload: EnterpriseContractPayload = {
    customerId: payload.customer_id,
    contractValue: payload.contract_value,
    billingInterval: payload.billing_interval,
    startDate: payload.start_date,
    ...(payload.end_date ? { endDate: payload.end_date } : {}),
    ...(payload.po_number ? { poNumber: payload.po_number } : {}),
    currency: 'USD',
  };
  const response = await authenticatedFetch<Envelope<EnterpriseContract>>(
    apiBaseUrl,
    token,
    `/admin/enterprise/contracts/${contractId}`,
    {
      method: 'PUT',
      body: JSON.stringify(requestPayload),
    },
  );
  return response.data;
}

export async function deleteContract(contractId: string, token: string): Promise<void> {
  const apiBaseUrl = window.location.origin;
  await authenticatedFetch<void>(apiBaseUrl, token, `/admin/enterprise/contracts/${contractId}`, {
    method: 'DELETE',
  });
}

export function useUnmatchedTransactions() {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<UnmatchedTransaction[], Error>({
    queryKey: ['admin', 'reconciliation', 'unmatched'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const response = await authenticatedFetch<CollectionEnvelope<UnmatchedTransaction>>(
        apiBaseUrl,
        token,
        '/admin/reconciliation/unmatched',
      );
      return response.data;
    },
    enabled: !!token,
  });
}

export async function matchTransaction(
  bankTransactionId: string,
  paymentId: string,
  token: string,
): Promise<void> {
  const apiBaseUrl = window.location.origin;
  await authenticatedFetch<void>(apiBaseUrl, token, '/admin/reconciliation/match', {
    method: 'POST',
    body: JSON.stringify({ bankTransactionId, paymentId }),
  });
}

// ---------------------------------------------------------------------------
// Dashboard Stats
// ---------------------------------------------------------------------------

export interface DashboardStats {
  customers: { total: number; new_30d: number; new_7d: number };
  subscriptions: { total: number; active: number; trialing: number; past_due: number; canceled: number; new_30d: number };
  invoices: { total: number; paid: number; outstanding: number; draft: number; total_collected: number; total_outstanding: number };
  credits: { accounts_with_credits: number; total_balance: number };
  plans: { total: number; active: number };
  contracts: { total: number };
  monthly_customers: Array<{ month: string; count: number }>;
  monthly_subscriptions: Array<{ month: string; count: number }>;
  monthly_revenue: Array<{ month: string; revenue: number }>;
  plan_distribution: Array<{ plan_name: string; subscriber_count: number }>;
}

export function useDashboardStats() {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<DashboardStats, Error>({
    queryKey: ['admin', 'dashboard', 'stats'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(`${apiBaseUrl}/admin/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Dashboard stats: ${res.statusText}`);
      const json = await res.json();
      return json.data;
    },
    enabled: !!token,
    refetchInterval: 60_000,
  });
}

export function useMrrReport() {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<MrrReportPoint[], Error>({
    queryKey: ['admin', 'reports', 'mrr'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const response = await authenticatedFetch<Envelope<{ totalCents: number }>>(
        apiBaseUrl,
        token,
        '/admin/reports/mrr',
      );
      return [{
        month: monthLabel(new Date()),
        mrr: response.data.totalCents / 100,
        newMrr: 0,
        expansionMrr: 0,
        contractionMrr: 0,
      }];
    },
    enabled: !!token,
  });
}

export function useArrReport() {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<ArrReportSummary, Error>({
    queryKey: ['admin', 'reports', 'arr-summary'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const [arrResponse, churnResponse] = await Promise.all([
        authenticatedFetch<Envelope<{ totalCents: number }>>(apiBaseUrl, token, '/admin/reports/arr'),
        authenticatedFetch<Envelope<{ churnRate: number }>>(
          apiBaseUrl,
          token,
          `/admin/reports/churn?start=${daysAgo(30)}&end=${daysAgo(0)}`,
        ),
      ]);
      return {
        arr: arrResponse.data.totalCents / 100,
        churnRate: churnResponse.data.churnRate,
      };
    },
    enabled: !!token,
  });
}

export function useCohortRetention() {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<CohortRetention[], Error>({
    queryKey: ['admin', 'reports', 'cohort-retention'],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const cohortStart = new Date();
      cohortStart.setMonth(cohortStart.getMonth() - 6);
      const cohortMonth = monthLabel(cohortStart);
      const response = await authenticatedFetch<Envelope<{
        cohortMonth: string;
        rows: Array<{ month: string; retentionRate: number }>;
      }>>(
        apiBaseUrl,
        token,
        `/admin/reports/cohorts?cohortMonth=${cohortMonth}`,
      );

      const values = response.data.rows.slice(0, 7).map((row) => Math.round(row.retentionRate * 100));
      return [{
        cohort: response.data.cohortMonth,
        month0: values[0] ?? 0,
        month1: values[1] ?? 0,
        month2: values[2] ?? 0,
        month3: values[3] ?? 0,
        month4: values[4] ?? 0,
        month5: values[5] ?? 0,
        month6: values[6] ?? 0,
      }];
    },
    enabled: !!token,
  });
}

export function useAuditLogs(query: AuditLogQuery = {}) {
  const { token, apiBaseUrl } = useAdminToken();
  return useQuery<AuditLogPage, Error>({
    queryKey: ['admin', 'audit-logs', query],
    queryFn: async () => {
      if (!token) throw new Error('Not authenticated');
      const params = new URLSearchParams();
      if (query.action) params.set('action', query.action);
      if (query.resource_type) params.set('resource_type', query.resource_type);
      if (query.actor_id) params.set('actor_id', query.actor_id);
      if (query.from) params.set('from', query.from);
      if (query.to) params.set('to', query.to);
      if (query.cursor) params.set('cursor', query.cursor);
      if (typeof query.limit === 'number') params.set('limit', String(query.limit));
      const suffix = params.toString() ? `?${params.toString()}` : '';
      return authenticatedFetch<AuditLogPage>(apiBaseUrl, token, `/admin/audit-logs${suffix}`);
    },
    enabled: !!token,
  });
}
